import type {
  BalanceLedgerEntry,
  CashoutRequest,
  Click,
  ConsentRecord,
  Market,
  Ticket,
  Transaction,
  User,
  WelcomeBonusFunding,
} from '../dataProvider/types';

/**
 * The DSAR export package (D-dsar, the Phase 7 lead). Assembling the member's OWN data into a
 * viewable read-only package IS the fulfilment deliverable in the mock (a real file export is
 * the migration seam, out of scope here). Same assemble-from-data seam as buildMemberEyeView,
 * but a DIFFERENT audience and so a DIFFERENT scope: a DSAR returns the member's data TO the
 * member, so it is the FULL admin-side record of their own data, NOT the floored member-facing
 * projection.
 *
 * THE INVENTORY PRINCIPLE (D-dsar): the member's OWN personal data IN, the platform's internal
 * operational records ABOUT them OUT. The legal scope is the platform's to confirm with
 * counsel, so this line is deliberately easy to move (one assembly module), NOT hard-coded as
 * if settled.
 *  - INCLUDE: the profile, transactions, cashouts INCLUDING payoutDetails (the member's own
 *    payout data, returned to them, unlike the impersonation projection that dropped it for a
 *    different audience), the balance ledger, clicks, tickets, welcome bonuses, the referral edge,
 *    and the consent history (D-consent-records; the member's own consent record is their data,
 *    added here by COMPOSITION, the adjustable inventory module doing its job, NOT a DSAR rebuild).
 *  - EXCLUDE: audit entries about them (the tamper-evidence record, naming admins and their
 *    actions - the platform's record, not the member's data) and impersonation sessions where
 *    they were the subject (an internal access log). These are NEVER fetched into the package.
 *  - HONESTLY NOTE, never fabricate: promo redemptions are not modelled per member (PromoCode
 *    carries only an aggregate usedCount, no code-to-user link), so the package says so plainly.
 */

export interface DsarExportProfile {
  id: number;
  name: string;
  email: string;
  market: Market;
  tier: User['tier'];
  signupMethod: User['signupMethod'];
  createdAt: string;
}

export interface DsarExportTransaction {
  id: number;
  merchantId: number;
  market: Market;
  grossAmount: number;
  cashbackAmount: number;
  currency: Transaction['currency'];
  status: Transaction['status'];
  occurredAt: string;
}

export interface DsarExportCashout {
  id: number;
  amount: number;
  currency: CashoutRequest['currency'];
  market: Market;
  destinationType: CashoutRequest['destinationType'];
  payoutMethod: CashoutRequest['payoutMethod'];
  /** The member's OWN payout envelope: included in a DSAR (returned to the member). */
  payoutDetails: string | null;
  status: CashoutRequest['status'];
  requestedAt: string;
}

export interface DsarExportLedgerEntry {
  id: number;
  amount: number;
  market: Market;
  kind: BalanceLedgerEntry['kind'];
  reason: string;
  createdAt: string;
}

export interface DsarExportClick {
  id: number;
  merchantId: number;
  market: Market;
  occurredAt: string;
  converted: boolean;
}

export interface DsarExportTicket {
  id: number;
  subject: string;
  status: Ticket['status'];
  createdAt: string;
}

export interface DsarExportBonus {
  id: number;
  amount: number;
  currency: WelcomeBonusFunding['currency'];
  fundedAt: string;
}

export interface DsarExportReferral {
  /** The referring member id, or null if this member was not referred. */
  referredBy: number | null;
  referredAt: string | null;
}

export interface DsarExportConsent {
  id: number;
  consentType: ConsentRecord['consentType'];
  action: ConsentRecord['action'];
  source: ConsentRecord['source'];
  policyVersion: string;
  occurredAt: string;
}

export interface DsarExportPackage {
  subjectUserId: number;
  profile: DsarExportProfile;
  transactions: DsarExportTransaction[];
  cashouts: DsarExportCashout[];
  ledgerEntries: DsarExportLedgerEntry[];
  clicks: DsarExportClick[];
  tickets: DsarExportTicket[];
  welcomeBonuses: DsarExportBonus[];
  referral: DsarExportReferral;
  /**
   * The member's consent history (D-consent-records): their own provable record of grants and
   * revocations, included by COMPOSITION (the consent module owns the surface; the DSAR export
   * references the same data, it does not rebuild it).
   */
  consentHistory: DsarExportConsent[];
  /**
   * Promo redemptions are NOT modelled per member: the package records that honestly rather
   * than inventing a list. `recorded` is always false today; the field exists so the package
   * states the gap explicitly.
   */
  promoRedemptions: { recorded: false };
}

export interface DsarExportInput {
  user: User;
  transactions: Transaction[];
  cashouts: CashoutRequest[];
  ledgerEntries: BalanceLedgerEntry[];
  clicks: Click[];
  tickets: Ticket[];
  welcomeBonuses: WelcomeBonusFunding[];
  consents: ConsentRecord[];
}

const byId = <T extends { id: number }>(a: T, b: T): number => a.id - b.id;

export function buildDsarExportPackage(
  input: DsarExportInput,
): DsarExportPackage {
  const { user } = input;
  return {
    subjectUserId: user.id,
    profile: {
      id: user.id,
      name: user.name,
      email: user.email,
      market: user.market,
      tier: user.tier,
      signupMethod: user.signupMethod,
      createdAt: user.createdAt,
    },
    transactions: input.transactions
      .map((t) => ({
        id: t.id,
        merchantId: t.merchantId,
        market: t.market,
        grossAmount: t.grossAmount,
        cashbackAmount: t.cashbackAmount,
        currency: t.currency,
        status: t.status,
        occurredAt: t.occurredAt,
      }))
      .sort(byId),
    cashouts: input.cashouts
      .map((c) => ({
        id: c.id,
        amount: c.amount,
        currency: c.currency,
        market: c.market,
        destinationType: c.destinationType,
        payoutMethod: c.payoutMethod,
        payoutDetails: c.payoutDetails,
        status: c.status,
        requestedAt: c.requestedAt,
      }))
      .sort(byId),
    ledgerEntries: input.ledgerEntries
      .map((e) => ({
        id: e.id,
        amount: e.amount,
        market: e.market,
        kind: e.kind,
        reason: e.reason,
        createdAt: e.createdAt,
      }))
      .sort(byId),
    clicks: input.clicks
      .map((c) => ({
        id: c.id,
        merchantId: c.merchantId,
        market: c.market,
        occurredAt: c.occurredAt,
        converted: c.converted,
      }))
      .sort(byId),
    tickets: input.tickets
      .map((t) => ({
        id: t.id,
        subject: t.subject,
        status: t.status,
        createdAt: t.createdAt,
      }))
      .sort(byId),
    welcomeBonuses: input.welcomeBonuses
      .map((w) => ({
        id: w.id,
        amount: w.amount,
        currency: w.currency,
        fundedAt: w.fundedAt,
      }))
      .sort(byId),
    referral: {
      referredBy: user.referredBy,
      referredAt: user.referredAt,
    },
    consentHistory: input.consents
      .map((c) => ({
        id: c.id,
        consentType: c.consentType,
        action: c.action,
        source: c.source,
        policyVersion: c.policyVersion,
        occurredAt: c.occurredAt,
      }))
      .sort(byId),
    promoRedemptions: { recorded: false },
  };
}
